import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { globalSearch } from "../api";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const queryParams = useQuery();

  const initialQuery = queryParams.get("q") || "";

  const [searchText, setSearchText] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [results, setResults] = useState({
    total: 0,
    active: [],
    dropped: [],
    deals: [],
  });

  const runSearch = async (value) => {
    const cleanValue = String(value || "").trim();

    if (cleanValue.length < 2) {
      setError("Please enter at least 2 characters");
      setResults({
        total: 0,
        active: [],
        dropped: [],
        deals: [],
      });
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await globalSearch(cleanValue);

      setResults({
        total: Number(response.data?.total || 0),
        active: Array.isArray(response.data?.active)
          ? response.data.active
          : [],
        dropped: Array.isArray(response.data?.dropped)
          ? response.data.dropped
          : [],
        deals: Array.isArray(response.data?.deals)
          ? response.data.deals
          : [],
      });
    } catch (err) {
      console.error("GLOBAL SEARCH PAGE ERROR:", err);

      setError(
        err.response?.data?.error ||
          "Could not search CRM records"
      );

      setResults({
        total: 0,
        active: [],
        dropped: [],
        deals: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchText(initialQuery);

    if (initialQuery.trim()) {
      runSearch(initialQuery);
    }
  }, [initialQuery]);

  const sections = useMemo(
    () => [
      {
        key: "active",
        title: "Active Leads",
        color: "#16A34A",
        items: results.active,
      },
      {
        key: "dropped",
        title: "Dropped Leads",
        color: "#DC2626",
        items: results.dropped,
      },
      {
        key: "deals",
        title: "Deals",
        color: "#7C3AED",
        items: results.deals,
      },
    ],
    [results]
  );

  const handleSubmit = (event) => {
    event.preventDefault();

    const cleanValue = searchText.trim();

    if (!cleanValue) return;

    navigate(`/search?q=${encodeURIComponent(cleanValue)}`);
  };

  const openRecord = (record, type) => {
    if (type === "deals" && record.deal_id) {
      navigate(`/deals/${record.deal_id}`);
      return;
    }

    if (type === "dropped") {
      navigate(`/drop/${record.id}`);
      return;
    }

    navigate(`/leads/${record.id}`);
  };

  return (
    <div
      style={{
        minHeight: "100%",
        padding: "24px",
        background: "#F6F7F9",
      }}
    >
      <div
        style={{
          maxWidth: 980,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            marginBottom: 20,
          }}
        >
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Global Lead Finder
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: "#6B7280",
            }}
          >
            Search active leads, dropped leads and deals from one place.
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <input
            className="form-input"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search phone, name, email, campaign..."
            autoFocus
            style={{
              flex: 1,
              height: 44,
            }}
          />

          <button
            type="submit"
            className="btn btn-primary"
            style={{
              minWidth: 110,
            }}
          >
            Search
          </button>
        </form>

        {error && (
          <div
            style={{
              padding: "12px 14px",
              marginBottom: 18,
              borderRadius: 8,
              background: "#FEE2E2",
              color: "#B91C1C",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading-screen">
            <div className="spinner" />
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 12,
                marginBottom: 22,
              }}
            >
              <SummaryCard
                label="Total Results"
                value={results.total}
                color="#111827"
              />

              <SummaryCard
                label="Active"
                value={results.active.length}
                color="#16A34A"
              />

              <SummaryCard
                label="Dropped"
                value={results.dropped.length}
                color="#DC2626"
              />

              <SummaryCard
                label="Deals"
                value={results.deals.length}
                color="#7C3AED"
              />
            </div>

            {results.total === 0 && initialQuery.trim() ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #E5E7EB",
                  color: "#6B7280",
                }}
              >
                No CRM record found.
              </div>
            ) : (
              sections.map((section) => {
                if (!section.items.length) return null;

                return (
                  <div
                    key={section.key}
                    style={{
                      marginBottom: 24,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: section.color,
                        }}
                      />

                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {section.title}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#6B7280",
                        }}
                      >
                        ({section.items.length})
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      {section.items.map((record) => (
                        <SearchResultCard
                          key={`${section.key}-${record.id}-${record.deal_id || ""}`}
                          record={record}
                          type={section.key}
                          color={section.color}
                          onOpen={() =>
                            openRecord(record, section.key)
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        padding: "14px 16px",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#6B7280",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 24,
          fontWeight: 700,
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SearchResultCard({
  record,
  type,
  color,
  onOpen,
}) {
  const typeLabel =
    type === "active"
      ? "Active Lead"
      : type === "dropped"
      ? "Dropped Lead"
      : "Deal";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 16,
        padding: 18,
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        boxShadow: "0 3px 12px rgba(0,0,0,0.04)",
      }}
    >
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            {record.name || "Unnamed Lead"}
          </div>

          <span
            style={{
              padding: "4px 9px",
              borderRadius: 20,
              background: `${color}18`,
              color,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {typeLabel}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(3, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <Info label="Phone" value={record.phone} />
          <Info label="Email" value={record.email} />

          <Info
            label="Stage"
            value={
              type === "deals"
                ? record.deal_stage || record.stage
                : record.stage
            }
          />

          <Info
            label="Assigned To"
            value={record.assigned_to_name}
          />

          <Info
            label="Source"
            value={record.source}
          />

          <Info
            label="Campaign"
            value={record.campaign_name}
          />

         <Info
  label="Lead Date"
  value={
    record.created_at
      ? new Date(record.created_at).toLocaleDateString("en-GB")
      : "-"
  }
/>

{type === "dropped" && (
  <Info
    label="Drop Date"
    value={
      record.dropped_at
        ? new Date(record.dropped_at).toLocaleDateString("en-GB")
        : "-"
    }
  />
)}

{type === "deals" && (
  <>
    <Info
      label="Site Visit Date"
      value={
        record.site_visit_date
          ? new Date(record.site_visit_date).toLocaleDateString("en-GB")
          : "-"
      }
    />

    <Info
      label="Meeting / Office Meeting Date"
      value={
        record.meeting_date
          ? new Date(record.meeting_date).toLocaleDateString("en-GB")
          : "-"
      }
    />
  </>
)}

          {type === "dropped" && (
            <Info
              label="Drop Reason"
              value={record.drop_reason}
            />
          )}

          {type === "deals" && (
            <Info
              label="Deal Value"
              value={
                record.deal_value
                  ? `₹${record.deal_value} Cr`
                  : "-"
              }
            />
          )}

          
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          className="btn btn-primary"
          onClick={onOpen}
        >
          Open
        </button>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#9CA3AF",
          marginBottom: 3,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 13,
          color: "#374151",
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </div>
    </div>
  );
}